/* ============================================================
   Habit schedule predicates — "is this habit due on this day?" plus the week
   windowing and the human-readable describer.

   Pure and clock-free: callers always pass `today`. All day math goes through
   the recurrence module's UTC-midnight helpers, so a schedule never consults the
   ambient offset or trips over DST.

   `weekStartsOn` is a `getDay()` index (0 = Sunday), the same encoding as
   `users.weekStartsOn` — so a weekly quota's window and the calendar UI always
   agree on where a week begins.
   ============================================================ */
import { addDaysYMD, ymdToUTC } from '../recurrence/dates';
import type { Weekday } from '../recurrence/types';
import {
  MAX_TIMES_PER_WEEK,
  MIN_TIMES_PER_WEEK,
  WEEKDAY_CODES,
  WEEKDAY_INDEX,
  WEEKDAY_LABELS,
  type HabitSchedule,
} from './types';

/** The RFC 5545 weekday code for a "YYYY-MM-DD" day. */
export function weekdayOf(day: string): Weekday {
  return WEEKDAY_CODES[ymdToUTC(day).getUTCDay()]!;
}

/**
 * Coerce a schedule into its canonical form, so one cadence has exactly one
 * representation. Dedupes/sorts weekday lists, clamps the weekly quota, and
 * collapses the degenerate cases (all 7 weekdays, or 7× per week) to `daily`.
 * An empty weekday list would mean "never due", which is not a habit — it also
 * collapses to `daily` rather than creating a row that can never be kept.
 */
export function normalizeSchedule(schedule: HabitSchedule): HabitSchedule {
  switch (schedule.kind) {
    case 'daily':
      return { kind: 'daily' };
    case 'weekdays': {
      const seen = new Set(schedule.days.filter((d) => d in WEEKDAY_INDEX));
      if (seen.size === 0 || seen.size === 7) return { kind: 'daily' };
      // Sort into calendar order (Sun..Sat) so equal sets compare equal.
      const days = WEEKDAY_CODES.filter((c) => seen.has(c));
      return { kind: 'weekdays', days: [...days] };
    }
    case 'timesPerWeek': {
      const times = Math.round(schedule.times);
      if (!Number.isFinite(times) || times >= 7) return { kind: 'daily' };
      return {
        kind: 'timesPerWeek',
        times: Math.min(MAX_TIMES_PER_WEEK, Math.max(MIN_TIMES_PER_WEEK, times)),
      };
    }
  }
}

/**
 * Should the habit surface on `day`?
 *
 * Nothing is due before the habit existed (`startsOn`) — that's what keeps days
 * predating the habit out of its consistency denominator instead of reading as a
 * long run of misses. Lexicographic compare is exact for "YYYY-MM-DD".
 *
 * A weekly quota is due EVERY day: the whole point is that you choose which days,
 * so the habit has to be offered in order to be chooseable. A fixed weekday list
 * is due only on its days — showing an unfilled row on a planned rest day would
 * manufacture a miss the user never agreed to.
 */
export function isDueOn(schedule: HabitSchedule, day: string, startsOn: string): boolean {
  if (day < startsOn) return false;
  switch (schedule.kind) {
    case 'daily':
      return true;
    case 'weekdays':
      return schedule.days.includes(weekdayOf(day));
    case 'timesPerWeek':
      return true;
  }
}

/** Inclusive first/last day of the week containing `day`. */
export function weekBounds(day: string, weekStartsOn: number): { start: string; end: string } {
  const dow = ymdToUTC(day).getUTCDay();
  const back = (dow - weekStartsOn + 7) % 7;
  const start = addDaysYMD(day, -back);
  return { start, end: addDaysYMD(start, 6) };
}

/** Every day in `[from, to]` inclusive. Empty when `to` precedes `from`. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDaysYMD(d, 1)) out.push(d);
  return out;
}

/**
 * How many days in `[from, to]` the habit is scheduled for. For a weekly quota
 * this is the quota itself (capped by how many days of the window are actually
 * available), not the number of days the habit was offered.
 *
 * `[from, to]` is treated as ONE quota window, so this is only meaningful for a
 * quota over a single week. For any longer span use `scheduledCountOverRange`,
 * which walks the weeks — otherwise a 3-a-week habit asks for 3 across a whole
 * month instead of ~13.
 */
export function scheduledCount(
  schedule: HabitSchedule,
  from: string,
  to: string,
  startsOn: string,
): number {
  const days = daysBetween(from < startsOn ? startsOn : from, to);
  if (schedule.kind === 'timesPerWeek') return Math.min(schedule.times, days.length);
  return days.filter((d) => isDueOn(schedule, d, startsOn)).length;
}

/**
 * How many days `[from, to]` asks for across a span of any length — the
 * denominator for a consistency read over a month, a year, or a heatmap column.
 *
 * A weekly quota is a PER-WEEK promise, so the span is walked one week at a time
 * and the quotas summed; a week that only partly overlaps the span asks for at
 * most the days it actually contributes (a 2-day tail can't owe 3 reps). Fixed
 * cadences have no period to sum, so they defer straight to `scheduledCount`.
 */
export function scheduledCountOverRange(
  schedule: HabitSchedule,
  from: string,
  to: string,
  startsOn: string,
  weekStartsOn: number,
): number {
  const start = from < startsOn ? startsOn : from;
  if (to < start) return 0;
  if (schedule.kind !== 'timesPerWeek') return scheduledCount(schedule, start, to, startsOn);
  let total = 0;
  let cursor = start;
  while (cursor <= to) {
    const { end } = weekBounds(cursor, weekStartsOn);
    const sliceEnd = end < to ? end : to;
    total += Math.min(schedule.times, daysBetween(cursor, sliceEnd).length);
    cursor = addDaysYMD(sliceEnd, 1);
  }
  return total;
}

/**
 * Plain-English cadence, e.g. "Every day", "Every weekday", "Mon, Wed & Fri",
 * "3 times a week". Rendered under the picker and in the habit row so a
 * cadence never has to be decoded from a stored shape — the same job the
 * RRULE humanizer does for tasks.
 */
export function describeSchedule(schedule: HabitSchedule, weekStartsOn = 1): string {
  switch (schedule.kind) {
    case 'daily':
      return 'Every day';
    case 'weekdays': {
      const set = new Set(schedule.days);
      if (set.size === 5 && !set.has('SA') && !set.has('SU')) return 'Every weekday';
      if (set.size === 2 && set.has('SA') && set.has('SU')) return 'Weekends';
      // List in the user's own week order, so a Monday-first user reads Mon first.
      const ordered = Array.from({ length: 7 }, (_, i) => (weekStartsOn + i) % 7)
        .filter((idx) => set.has(WEEKDAY_CODES[idx]!))
        .map((idx) => WEEKDAY_LABELS[idx]!);
      return joinWithAnd(ordered);
    }
    case 'timesPerWeek':
      if (schedule.times === 1) return 'Once a week';
      if (schedule.times === 2) return 'Twice a week';
      return `${schedule.times} times a week`;
  }
}

/** "Mon", "Mon & Wed", "Mon, Wed & Fri". */
function joinWithAnd(parts: readonly string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]!}`;
}
